import {
  assertGetEvaluator,
  getActiveEvaluatorEvaluations,
  getActiveFunders,
  getActiveProposals,
  getActiveRecommenders,
  getEvaluableRequestMax,
  getEvaluatorTrancheList,
} from "@engine/round/accessors";
import {
  Funder,
  Node,
  Proposal,
  Recommender,
  RoundAggregate,
  matchLimit,
} from "@engine/round/aggregate";
import { Evaluator } from "@lib/types";
import { assert } from "@lib/utils/utils";
import { matchingMultipliersForEvaluators } from "algorithm/matching";
import Matrix from "algorithm/matrix";
import { multiLayerSProcess } from "algorithm/multiLayerSProcess";
import Vector from "algorithm/vector";
import { getEvaluableIndicesMap } from "./getEvaluableIndicesMap";
import { trancheListToAlgoTranches } from "./trancheListToAlgoInterface";

export type SProcessInput = {
  parentNodes: Node[];
  layerNodes: [Node[], Node[]];
  parentBudgets: Vector<"funder">;
  layerPreferenceLists: [
    Matrix<"tranche", "recommender+hold">[],
    Matrix<"tranche", "proposal+hold">[],
  ];
  holdProposalId: string;
};

export type SProcessOutput = {
  parentNodes: Node[];
  layerNodes: [Node[], Node[]];
  layerTargetTotals: [Vector<"recommender+hold">, Vector<"proposal+hold">]; // total amount allocated to each evaluable (in each layer)
  layerAccounts: [
    Matrix<"funder", "recommender+hold">,
    Matrix<"recommender+hold", "proposal+hold">,
  ];
  layerGuarantees: [
    Matrix<"funder", "recommender+hold">,
    Matrix<"recommender+hold", "proposal+hold">,
  ];
  layerBudgets: [Vector<"funder">, Vector<"recommender">];
  holdProposalId: string;
};

export type FunderLayerInput = {
  funderPreferenceLists: Matrix<"tranche", "recommender+hold">[];
  funderBudgets: Vector<"funder">;
  funders: Funder[];
};

export type RecommenderLayerInput = {
  recommenderPreferenceLists: Matrix<"tranche", "proposal+hold">[];
  orderedRecommenders: Recommender[];
  orderedProposals: Proposal[];
  recommenderIndicesMap: { [recommenderId: string]: number };
  proposalIndicesMap: { [proposalId: string]: number };
  holdProposal: Proposal;
};

function runSProcess(input: SProcessInput): SProcessOutput {
  const numLayers = input.layerPreferenceLists.length;
  const output: {
    layerAccounts: [
      Matrix<"funder", "recommender+hold">,
      Matrix<"recommender+hold", "proposal+hold">,
    ];
    layerGuarantees: [
      Matrix<"funder", "recommender+hold">,
      Matrix<"recommender+hold", "proposal+hold">,
    ];
    totalEpochs: number;
    layerBudgets: [Vector<"funder">, Vector<"recommender">];
  } = multiLayerSProcess(input.parentBudgets, [
    input.layerPreferenceLists[0],
    input.layerPreferenceLists[1],
  ]);
  assert(numLayers === 2);
  assert(output.layerAccounts.length === numLayers);
  assert(output.layerGuarantees.length === numLayers);
  assert(output.layerBudgets.length === numLayers);

  const layerTargetTotals: [
    Vector<"recommender+hold">,
    Vector<"proposal+hold">,
  ] = [output.layerAccounts[0].sum(0), output.layerAccounts[1].sum(0)];

  assert(input.layerNodes.length === 2);
  assert(output.layerBudgets.length === 2);
  assert(
    input.layerNodes[0].length === output.layerBudgets[1].length + 1,
    "There should be a budget for every evaluator but Hold",
  );

  return {
    parentNodes: input.parentNodes,
    layerNodes: input.layerNodes,
    holdProposalId: input.holdProposalId,
    layerTargetTotals,
    layerAccounts: output.layerAccounts,
    layerGuarantees: output.layerGuarantees,
    layerBudgets: output.layerBudgets,
  };
}

function getSProcessInputFromRound(round: RoundAggregate): SProcessInput {
  const recommenderLayerInput = getRecommenderLayerInput(round);
  const numRecommenders = recommenderLayerInput.orderedRecommenders.length;
  const numProposals = recommenderLayerInput.orderedProposals.length;
  assert(
    recommenderLayerInput.recommenderPreferenceLists.length === numRecommenders,
  );
  assert(
    round.holdProposalId in recommenderLayerInput.proposalIndicesMap,
    "holdProposalId should be in proposalIndicesMap",
  );
  for (let i = 0; i < numRecommenders; i++) {
    assert(
      recommenderLayerInput.recommenderPreferenceLists[i].n_cols ===
        numProposals,
      "recommenderPreferenceLists[i].n_cols should be equal to numProposals",
    );
  }

  const funderLayerInput = getFunderLayerInput(
    round,
    recommenderLayerInput.recommenderIndicesMap,
  );

  const numFunders = funderLayerInput.funders.length;
  assert(
    funderLayerInput.funderPreferenceLists.length === numFunders,
    "funderPreferenceLists.length should be equal to numFunders",
  );
  for (let i = 0; i < numFunders; i++) {
    assert(
      funderLayerInput.funderPreferenceLists[i].n_cols === numRecommenders + 1,
    ); // +1 for the hold proposal
  }

  const input = sProcessInputFromLayers(
    funderLayerInput,
    recommenderLayerInput,
  );
  assert(input.parentNodes.length === numFunders);
  assert(input.parentBudgets.length === numFunders);
  assert(input.layerNodes.length === 2);
  assert(input.layerPreferenceLists.length === 2);
  assert(input.layerPreferenceLists[0].length === numFunders);
  for (let i = 0; i < input.layerPreferenceLists[0].length; i++) {
    assert(input.layerPreferenceLists[0][i].n_cols === numRecommenders + 1); // +1 for the hold proposal
  }
  assert(input.layerPreferenceLists[1].length === numRecommenders);
  for (let i = 0; i < input.layerPreferenceLists[1].length; i++) {
    assert(input.layerPreferenceLists[1][i].n_cols === numProposals);
  }

  return input;
}

export function runSProcessOnRound(round: RoundAggregate): SProcessOutput {
  const input = getSProcessInputFromRound(round);
  return runSProcess(input);
}

export function runSProcessOnRoundWithMatchingAdjustment(
  round: RoundAggregate,
): {
  initialSProcessOutput: SProcessOutput;
  sProcessOutputWithMatchingAdjustment: SProcessOutput;
  multipliers: Matrix<"proposal", "evaluator">;
} {
  const initialSProcessInput = getSProcessInputFromRound(round);
  const initialSProcessOutput = runSProcess(initialSProcessInput);

  const activeProposals = getActiveProposals(round);

  const evaluatorIds = initialSProcessOutput.layerNodes[0]
    .map((node) => node.id)
    .filter((id) => id !== round.holdProposalId);

  const evaluators = evaluatorIds.map((evaluatorId) =>
    assertGetEvaluator(round, evaluatorId),
  );

  const expectedMatchingFractions: {
    [evaluatorId: string]: { [proposalId: string]: number };
  } = Object.fromEntries(evaluators.map((evaluator) => [evaluator.id, {}]));

  const apps: {
    id: string;
    matchLimit: number | null;
    matchRate: number;
    targetOutsideDonations: number | undefined;
    matchStartThreshold: number;
  }[] = activeProposals.map((proposal) => {
    const typedFields = proposal.typedFields;
    const matchRate = typedFields.matchRate ?? 0;
    for (const evaluator of evaluators) {
      const expectedMatchingFraction =
        evaluator.evaluations[proposal.id]?.expectedMatchingFraction ?? 0;
      expectedMatchingFractions[evaluator.id][proposal.id] =
        expectedMatchingFraction;
    }
    return {
      id: proposal.id,
      matchLimit: matchLimit(proposal) ?? null,
      matchRate,
      targetOutsideDonations: typedFields.targetOutsideDonations,
      matchStartThreshold: typedFields.baselineStandardGrantAmount ?? 0,
    };
  });

  const trancheProfiles = Object.fromEntries(
    evaluators.map((evaluator) => [
      evaluator.id,
      getEvaluatorTrancheList(round, evaluator),
    ]),
  );

  const evaluableMaxAllocations: { [evaluableId: string]: number | null } = {};
  for (const proposal of activeProposals) {
    evaluableMaxAllocations[proposal.id] = getEvaluableRequestMax(
      round,
      proposal.id,
    );
  }

  const multipliers = matchingMultipliersForEvaluators(
    initialSProcessOutput.layerBudgets[1].rename("recommender -> evaluator"),
    expectedMatchingFractions,
    evaluableMaxAllocations,
    trancheProfiles,
    apps,
    round.holdProposalId,
  );

  const layerPreferenceLists: [
    Matrix<"tranche", "recommender+hold">[],
    Matrix<"tranche", "proposal+hold">[],
  ] = [
    initialSProcessInput.layerPreferenceLists[0],
    initialSProcessInput.layerPreferenceLists[1].map(
      (preferenceList, evaluatorIndex) =>
        preferenceList.mulColumns(multipliers.getCol(evaluatorIndex)),
    ),
  ];

  const numRecommendersWithHold = initialSProcessInput.layerNodes[0].length;
  const numProposalsWithHold = activeProposals.length;
  assert(
    layerPreferenceLists[0].every(
      (trancheTable) => trancheTable.n_cols === numRecommendersWithHold,
    ),
  );
  assert(
    layerPreferenceLists[1].every(
      (trancheTable) => trancheTable.n_cols === numProposalsWithHold,
    ),
  );

  const input: SProcessInput = {
    parentNodes: initialSProcessInput.parentNodes,
    layerNodes: initialSProcessInput.layerNodes,
    parentBudgets: initialSProcessInput.parentBudgets,
    layerPreferenceLists,
    holdProposalId: initialSProcessInput.holdProposalId,
  };

  const multipliersWithoutHold = new Matrix<"proposal", "evaluator">(
    multipliers.toArrays().slice(1),
  );

  return {
    initialSProcessOutput,
    sProcessOutputWithMatchingAdjustment: runSProcess(input),
    multipliers: multipliersWithoutHold,
  };
}

function sProcessInputFromLayers(
  funderLayerInput: FunderLayerInput,
  recommenderLayerInput: RecommenderLayerInput,
): SProcessInput {
  return {
    parentNodes: funderLayerInput.funders,
    layerNodes: [
      [
        recommenderLayerInput.holdProposal,
        ...recommenderLayerInput.orderedRecommenders,
      ],
      recommenderLayerInput.orderedProposals,
    ],
    parentBudgets: funderLayerInput.funderBudgets,
    layerPreferenceLists: [
      funderLayerInput.funderPreferenceLists,
      recommenderLayerInput.recommenderPreferenceLists,
    ],
    holdProposalId: recommenderLayerInput.holdProposal.id,
  };
}

export function getRecommenderLayerInput(
  round: RoundAggregate,
): RecommenderLayerInput {
  const recommenders = getActiveRecommenders(round);
  const recommenderIds = recommenders.map((recommender) => recommender.id);
  const recommenderIndicesMap = getEvaluableIndicesMap(
    recommenderIds,
    round.holdProposalId,
  );

  const orderedRecommenders: Recommender[] = [];
  for (const recommender of recommenders) {
    if (!recommenderIndicesMap[recommender.id]) {
      continue;
    }

    orderedRecommenders[recommenderIndicesMap[recommender.id] - 1] =
      recommender;
  }

  const proposals = getActiveProposals(round);
  const proposalIds = proposals.map((proposal) => proposal.id);
  const proposalIndicesMap = getEvaluableIndicesMap(
    proposalIds,
    round.holdProposalId,
  );

  const proposalMax: { [evaluableId: string]: number | null } = {};
  for (const proposal of proposals) {
    proposalMax[proposal.id] = getEvaluableRequestMax(round, proposal.id);
  }

  const recommenderPreferenceLists = getPreferenceLists(
    round,
    orderedRecommenders,
    proposalMax,
    proposalIndicesMap,
  );

  const proposalsMap: { [proposalId: string]: Proposal } = {};
  for (const proposal of proposals) {
    proposalsMap[proposal.id] = proposal;
  }

  const orderedProposals: Proposal[] = [];
  for (const [proposalId, index] of Object.entries(proposalIndicesMap)) {
    orderedProposals[index] = proposalsMap[proposalId];
  }

  return {
    proposalIndicesMap,
    recommenderPreferenceLists: recommenderPreferenceLists.map((m) =>
      m.rename("tranche -> tranche", "evaluable+hold -> proposal+hold"),
    ),
    orderedRecommenders,
    orderedProposals,
    // TODO: returning recommenderIndicesMap should not be necessary, since the
    // same information is already available in the orderedRecommenders array.
    // It would be best to simplify this whenerever we get around to having a
    // more disciplined way of inserting hold.
    recommenderIndicesMap,
    // similarly we should not need to explicitly return the hold proposal
    holdProposal: proposalsMap[round.holdProposalId],
  };
}

function getFunderLayerInput(
  round: RoundAggregate,
  recommenderIndicesMap: { [recommenderId: string]: number },
): FunderLayerInput {
  const funders = getActiveFunders(round);

  return {
    funders,
    funderPreferenceLists: getPreferenceLists(
      round,
      funders,
      {},
      recommenderIndicesMap,
    ).map((m) =>
      m.rename("tranche -> tranche", "evaluable+hold -> recommender+hold"),
    ),
    funderBudgets: new Vector(funders.map((funder) => funder.budget)),
  };
}

export function getPreferenceLists(
  round: RoundAggregate,
  evaluators: Evaluator[],
  evaluableMaxAllocations: { [evaluableId: string]: number | null },
  evaluableIndicesMap: { [evaluableId: string]: number },
): Matrix<"tranche", "evaluable+hold">[] {
  const evaluatorPreferenceLists: Matrix<"tranche", "evaluable+hold">[] = [];
  const numEvaluables = Object.keys(evaluableIndicesMap).length;

  for (let i = 0; i < evaluators.length; i++) {
    const evaluator = evaluators[i];

    const activeEvaluations = getActiveEvaluatorEvaluations(round, evaluator);
    const activeEvaluableIds = activeEvaluations.map(
      (evaluation) => evaluation.evaluableId,
    );
    const deferredEvaluableIds =
      evaluator.type === "recommender"
        ? Object.values(evaluator.evaluations)
            .filter((evaluation) => evaluation.state === "deferred")
            .map((evaluation) => evaluation.evaluableId)
        : [];

    const evaluatorTrancheList = getEvaluatorTrancheList(round, evaluator);

    const evaluatorPreferenceList = trancheListToAlgoTranches(
      evaluatorTrancheList,
      evaluableIndicesMap,
      evaluableMaxAllocations,
      new Set([...activeEvaluableIds, ...deferredEvaluableIds]),
    );

    assert(
      evaluatorPreferenceList.n_cols === numEvaluables,
      "evaluatorPreferenceList.n_cols should be equal to numEvaluables",
    );
    evaluatorPreferenceLists[i] = evaluatorPreferenceList;
  }

  return evaluatorPreferenceLists;
}
